import axios from "axios";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import { Icon } from "@iconify/react";

const CheckOut = () => {
  const [cartItems, setCartItems] = useState([]);
  const storedCartitem = localStorage.getItem("cart");
  const navigate = useNavigate();

  const [shippingCost, setShippingCost] = useState(0);
  const [taxRate, setTaxRate] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchOrderDetails();
  }, []);

  const fetchOrderDetails = async () => {
    try {
      const response = await axios.get(
        "https://servercareseebd.careseebd.com/api/v1/orderDetails/getOrderDetails"
      );

      if (response.data.status === "success" && response.data.data.length > 0) {
        const { shippingCost, taxRate } = response.data.data[0];
        setShippingCost(shippingCost);
        setTaxRate(taxRate);
      } else {
        console.error("Error fetching order details:", response.data);
      }
    } catch (error) {
      console.error("API request failed:", error);
    }
  };

  useEffect(() => {
    if (storedCartitem) {
      const pasedCartItems = JSON.parse(storedCartitem);
      setCartItems(pasedCartItems);
    }
  }, [storedCartitem]);

  const calculateSubtotal = () => {
    return cartItems.reduce(
      (total, product) => total + product.price * product.quantity,
      0
    );
  };

  const calculateShipping = () => {
    return shippingCost;
  };

  const calculateTax = () => {
    return (calculateSubtotal() * taxRate) / 100;
  };

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    const shipping = calculateShipping();
    const tax = calculateTax();
    return subtotal + shipping + tax;
  };

  const subtotal = calculateSubtotal();
  const shipping = calculateShipping();
  const tax = calculateTax();
  const total = calculateTotal();

  const [formData, setFormData] = useState({
    name: "",
    address: "",
    thana: "",
    district: "",
    country: "",
    phone: "",
    email: "",
    orderNotes: "",
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    setSubmitting(true);
    try {
      await axios.post("https://servercareseebd.careseebd.com/api/v1/orders", {
        ...formData,
        cartItems: cartItems,
        subtotal,
        shipping,
        tax,
        total,
      });

      toast.success("Order Placed Successfully!");
      localStorage.removeItem("cart");
      navigate(-2);
    } catch (error) {
      toast.error("Error Creating Order");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-slate-50 min-h-screen pb-24 relative">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_50%_at_50%_0%,rgba(99,102,241,0.04),transparent)] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 relative z-10">

        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-flex items-center gap-1.5 bg-indigo-50 text-indigo-600 border border-indigo-100 rounded-full px-4 py-1 text-xs font-semibold uppercase tracking-widest mb-4">
            <Icon icon="solar:card-2-bold-duotone" className="w-3.5 h-3.5" />
            Checkout Page
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold text-gray-900 tracking-tight mb-4">
            Delivery & <span className="text-gradient">Billing Details</span>
          </h1>
          <p className="text-base text-gray-500 leading-relaxed max-w-xl mx-auto">
            Please fill in the fields below to confirm your shipping address and finalize your order.
          </p>
        </div>

        <form onSubmit={handleFormSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">

            {/* Form Area */}
            <div className="lg:col-span-2 bg-white border border-gray-100 rounded-3xl p-6 sm:p-8 shadow-sm">
              <h2 className="font-heading text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <Icon icon="solar:user-id-bold-duotone" className="w-5 h-5 text-indigo-500" />
                Shipping Information
              </h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {/* Name */}
                <div className="sm:col-span-2">
                  <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="e.g. Ashik Ahmed"
                    required
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 text-sm font-semibold text-gray-800"
                  />
                </div>

                {/* Country */}
                <div>
                  <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">
                    Country / Region *
                  </label>
                  <input
                    type="text"
                    name="country"
                    placeholder="e.g. Bangladesh"
                    value={formData.country}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 text-sm font-semibold text-gray-800"
                  />
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    placeholder="e.g. +8801700000000"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 text-sm font-semibold text-gray-800"
                  />
                </div>

                {/* Address */}
                <div className="sm:col-span-2">
                  <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">
                    Street Address *
                  </label>
                  <input
                    type="text"
                    placeholder="House, road number, landmark etc."
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 text-sm font-semibold text-gray-800"
                  />
                </div>

                {/* Thana */}
                <div>
                  <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">
                    Thana / Upazila *
                  </label>
                  <input
                    type="text"
                    name="thana"
                    placeholder="Enter Thana"
                    value={formData.thana}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 text-sm font-semibold text-gray-800"
                  />
                </div>

                {/* District */}
                <div>
                  <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">
                    District *
                  </label>
                  <input
                    type="text"
                    name="district"
                    placeholder="Enter District"
                    value={formData.district}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 text-sm font-semibold text-gray-800"
                  />
                </div>

                {/* Email */}
                <div className="sm:col-span-2">
                  <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">
                    Email Address (optional)
                  </label>
                  <input
                    type="email"
                    name="email"
                    placeholder="e.g. mail@example.com"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 text-sm font-semibold text-gray-800"
                  />
                </div>
              </div>

              {/* Order Notes */}
              <div className="mt-6">
                <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">
                  Order Notes (optional)
                </label>
                <textarea
                  name="orderNotes"
                  value={formData.orderNotes}
                  onChange={handleInputChange}
                  rows="4"
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 text-sm font-medium text-gray-800"
                  placeholder="Notes about your delivery, e.g. special instructions for the carrier."
                />
              </div>
            </div>

            {/* Sidebar Side */}
            <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-sm">
              <h2 className="font-heading text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
                <Icon icon="solar:bill-list-bold-duotone" className="w-5 h-5 text-indigo-500" />
                Your Order
              </h2>

              {/* Products list snapshot */}
              <div className="space-y-4 mb-6 max-h-52 overflow-y-auto pr-1">
                {cartItems.map((product) => (
                  <div key={product._id} className="flex justify-between items-center text-sm gap-2">
                    <span className="text-gray-600 font-medium truncate flex-1">
                      {product.name}
                    </span>
                    <span className="text-gray-400 text-xs whitespace-nowrap">
                      {product.quantity} ×
                    </span>
                    <span className="text-gray-800 font-semibold whitespace-nowrap">
                      ৳{product.price}
                    </span>
                  </div>
                ))}
              </div>

              {/* Divider */}
              <div className="w-full h-px bg-gray-100 my-4" />

              {/* Pricing calculations */}
              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Subtotal</span>
                  <span className="font-semibold text-gray-800">৳{subtotal}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Shipping Cost</span>
                  <span className="font-semibold text-gray-800">৳{shipping}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Vat & Tax ({taxRate}%)</span>
                  <span className="font-semibold text-gray-800">৳{tax}</span>
                </div>
              </div>

              {/* Divider */}
              <div className="w-full h-px bg-gray-100 my-4" />

              {/* Total price */}
              <div className="flex justify-between items-baseline mb-8">
                <span className="text-sm font-bold text-gray-900">Total Price</span>
                <span className="text-2xl font-extrabold font-heading text-indigo-600">
                  ৳{total}
                </span>
              </div>

              <button
                type="submit"
                disabled={submitting || cartItems.length === 0}
                className="w-full py-3.5 px-4 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-100 disabled:text-gray-400 text-white text-xs font-bold rounded-xl transition-all uppercase tracking-wider flex items-center justify-center gap-2 cursor-pointer shadow-lg shadow-indigo-600/10"
              >
                {submitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Placing Order...
                  </>
                ) : (
                  <>
                    <Icon icon="solar:check-circle-bold" className="w-4 h-4" />
                    Place Order
                  </>
                )}
              </button>
            </div>

          </div>
        </form>
      </div>
    </div>
  );
};

export default CheckOut;
